import { makeEnvelope } from './envelope';
import { normalizeCoords } from './coords';
import type { Modifiers, Send, WireMouse, WireMouseButton, WireWheel } from './types';

const BUTTON_MAP: Record<number, WireMouseButton> = {
  0: 'left',
  1: 'middle',
  2: 'right',
  3: 'back',
  4: 'forward',
};

function eventModifiers(e: MouseEvent | WheelEvent): Modifiers {
  return { alt: e.altKey, ctrl: e.ctrlKey, meta: e.metaKey, shift: e.shiftKey };
}

// attachPointer 把 target 上的鼠标 / 指针 / 滚轮事件映射到 wire event。
// 只处理 pointerType === 'mouse' 或 'pen';touch 由 attachTouch 单独处理。
export function attachPointer(target: HTMLElement, send: Send): () => void {
  const onDown = (e: PointerEvent) => {
    if (e.pointerType === 'touch') return;
    if (typeof target.setPointerCapture === 'function') {
      try {
        target.setPointerCapture(e.pointerId);
      } catch {
        /* ignore when already captured */
      }
    }
    const { x, y } = normalizeCoords(target, e.clientX, e.clientY);
    send(
      makeEnvelope<WireMouse>('input.mouse', {
        type: 'mousePressed',
        x,
        y,
        button: BUTTON_MAP[e.button] ?? 'left',
        buttons: e.buttons,
        click_count: e.detail || 1,
        modifiers: eventModifiers(e),
      }),
    );
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerType === 'touch') return;
    const { x, y } = normalizeCoords(target, e.clientX, e.clientY);
    send(
      makeEnvelope<WireMouse>('input.mouse', {
        type: 'mouseMoved',
        x,
        y,
        button: e.buttons > 0 ? (BUTTON_MAP[e.button] ?? 'left') : 'none',
        buttons: e.buttons,
        modifiers: eventModifiers(e),
      }),
    );
  };

  const onUp = (e: PointerEvent) => {
    if (e.pointerType === 'touch') return;
    const { x, y } = normalizeCoords(target, e.clientX, e.clientY);
    send(
      makeEnvelope<WireMouse>('input.mouse', {
        type: 'mouseReleased',
        x,
        y,
        button: BUTTON_MAP[e.button] ?? 'left',
        buttons: e.buttons,
        click_count: e.detail || 1,
        modifiers: eventModifiers(e),
      }),
    );
  };

  const onContextMenu = (e: Event) => {
    // 禁止浏览器自己的右键菜单;右键已作为 mouse 事件发出。
    e.preventDefault();
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const { x, y } = normalizeCoords(target, e.clientX, e.clientY);
    send(
      makeEnvelope<WireWheel>('input.wheel', {
        x,
        y,
        delta_x: e.deltaX,
        delta_y: e.deltaY,
        modifiers: eventModifiers(e),
      }),
    );
  };

  target.addEventListener('pointerdown', onDown);
  target.addEventListener('pointermove', onMove);
  target.addEventListener('pointerup', onUp);
  target.addEventListener('pointercancel', onUp);
  target.addEventListener('contextmenu', onContextMenu);
  target.addEventListener('wheel', onWheel, { passive: false });

  return () => {
    target.removeEventListener('pointerdown', onDown);
    target.removeEventListener('pointermove', onMove);
    target.removeEventListener('pointerup', onUp);
    target.removeEventListener('pointercancel', onUp);
    target.removeEventListener('contextmenu', onContextMenu);
    target.removeEventListener('wheel', onWheel);
  };
}
